import { useEffect, useState } from "react";
import { api, formatCurrency, getErrorMessage, monthNames } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Target, Trophy } from "lucide-react";
import { toast } from "sonner";

/**
 * Manage per-seller annual & monthly objectives + bonuses.
 * Props: open, onOpenChange, seller
 */
export const SellerObjectivesDialog = ({ open, onOpenChange, seller }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [objectives, setObjectives] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [annualValue, setAnnualValue] = useState("");
  const [monthlyValues, setMonthlyValues] = useState(Array(12).fill(""));
  const [bonusForm, setBonusForm] = useState({ threshold_amount: "", bonus_amount: "", description: "" });

  const load = async () => {
    if (!seller?.id) return;
    try {
      setLoading(true);
      const [objRes, bonRes] = await Promise.all([
        api.getAnnualObjectives({ year, seller_id: seller.id }),
        api.getSellerBonuses({ year, seller_id: seller.id }),
      ]);
      const list = objRes.data || [];
      setObjectives(list);
      setBonuses(bonRes.data || []);

      const annual = list.find((o) => !o.month);
      setAnnualValue(annual ? String(annual.target_ca) : "");
      const m = Array(12).fill("");
      list.forEach((o) => {
        if (o.month) m[o.month - 1] = String(o.target_ca);
      });
      setMonthlyValues(m);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, seller?.id, year]);

  const saveAnnual = async () => {
    const v = parseFloat(annualValue);
    if (isNaN(v) || v < 0) { toast.error("Valeur invalide"); return; }
    try {
      await api.createAnnualObjective({
        year, seller_id: seller.id, target_ca: v, objective_type: "seller", month: null,
      });
      toast.success("Objectif annuel enregistré");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const saveMonth = async (m) => {
    const raw = monthlyValues[m];
    if (raw === "" || raw === null) return;
    const v = parseFloat(raw);
    if (isNaN(v) || v < 0) { toast.error("Valeur invalide"); return; }
    try {
      await api.createAnnualObjective({
        year, seller_id: seller.id, target_ca: v, objective_type: "seller", month: m + 1,
      });
      toast.success(`${monthNames[m]} enregistré`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const deleteObjective = async (id) => {
    if (!window.confirm("Supprimer cet objectif ?")) return;
    try {
      await api.deleteAnnualObjective(id);
      toast.success("Objectif supprimé");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const addBonus = async () => {
    const t = parseFloat(bonusForm.threshold_amount);
    const b = parseFloat(bonusForm.bonus_amount);
    if (isNaN(t) || isNaN(b) || t < 0 || b < 0) { toast.error("Seuil et prime requis"); return; }
    try {
      await api.createSellerBonus({
        seller_id: seller.id,
        year,
        threshold_amount: t,
        bonus_amount: b,
        description: bonusForm.description || null,
      });
      toast.success("Prime ajoutée");
      setBonusForm({ threshold_amount: "", bonus_amount: "", description: "" });
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const deleteBonus = async (id) => {
    if (!window.confirm("Supprimer cette prime ?")) return;
    try {
      await api.deleteSellerBonus(id);
      toast.success("Prime supprimée");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const years = [new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1];
  const annualObj = objectives.find((o) => !o.month);
  const monthlyObjs = Object.fromEntries(objectives.filter((o) => o.month).map((o) => [o.month, o]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="seller-objectives-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Objectifs & Primes — {seller?.name}</DialogTitle>
          <DialogDescription>
            Saisissez les objectifs annuels ou mensuels et les paliers de prime. Ces données alimentent l'onglet <strong>Objectifs & Primes</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <Label className="text-sm">Année</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[100px]" data-testid="obj-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs defaultValue="objectives">
            <TabsList>
              <TabsTrigger value="objectives" data-testid="tab-objectives">
                <Target className="h-4 w-4 mr-1" /> Objectifs
              </TabsTrigger>
              <TabsTrigger value="bonuses" data-testid="tab-bonuses">
                <Trophy className="h-4 w-4 mr-1" /> Primes ({bonuses.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="objectives" className="space-y-6 pt-4">
              <div className="p-3 rounded-sm border bg-primary/5">
                <Label className="text-sm font-medium">Objectif annuel ({year})</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="Ex: 300000"
                    value={annualValue}
                    onChange={(e) => setAnnualValue(e.target.value)}
                    data-testid="annual-objective-input"
                  />
                  <Button onClick={saveAnnual} data-testid="save-annual-objective">
                    Enregistrer
                  </Button>
                  {annualObj && (
                    <Button variant="ghost" size="icon" onClick={() => deleteObjective(annualObj.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
                {annualObj && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Actuel : <strong>{formatCurrency(annualObj.target_ca)}</strong>
                  </p>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium">Objectifs mensuels ({year})</Label>
                <p className="text-xs text-muted-foreground mb-2">Laissez vide un mois pour ne pas définir d'objectif spécifique.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {monthNames.map((m, i) => (
                    <div key={m} className="flex items-center gap-1 p-2 rounded-sm border" data-testid={`month-${i}`}>
                      <span className="text-xs w-8">{m.slice(0, 3)}</span>
                      <Input
                        type="number"
                        min="0"
                        step="100"
                        placeholder="-"
                        value={monthlyValues[i]}
                        onChange={(e) => {
                          const next = [...monthlyValues];
                          next[i] = e.target.value;
                          setMonthlyValues(next);
                        }}
                        onBlur={() => {
                          if (monthlyValues[i] !== "") saveMonth(i);
                        }}
                        className="h-8 text-xs"
                        data-testid={`month-input-${i}`}
                      />
                      {monthlyObjs[i + 1] && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => deleteObjective(monthlyObjs[i + 1].id)}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Somme mensuelle saisie :{" "}
                  <strong>{formatCurrency(monthlyValues.reduce((s, v) => s + (parseFloat(v) || 0), 0))}</strong>
                </p>
              </div>
            </TabsContent>

            <TabsContent value="bonuses" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 border rounded-sm">
                <div>
                  <Label className="text-xs">Seuil CA (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={bonusForm.threshold_amount}
                    onChange={(e) => setBonusForm({ ...bonusForm, threshold_amount: e.target.value })}
                    data-testid="bonus-threshold"
                  />
                </div>
                <div>
                  <Label className="text-xs">Prime (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={bonusForm.bonus_amount}
                    onChange={(e) => setBonusForm({ ...bonusForm, bonus_amount: e.target.value })}
                    data-testid="bonus-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={bonusForm.description}
                    onChange={(e) => setBonusForm({ ...bonusForm, description: e.target.value })}
                    placeholder="Optionnel"
                    data-testid="bonus-description"
                  />
                </div>
                <div className="sm:col-span-3">
                  <Button onClick={addBonus} size="sm" data-testid="add-bonus-btn">
                    <Plus className="h-4 w-4 mr-1" /> Ajouter la prime
                  </Button>
                </div>
              </div>

              {bonuses.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune prime définie pour {year}.</p>
              ) : (
                <div className="space-y-2">
                  {bonuses.map((b) => (
                    <div key={b.id} className="flex items-center gap-2 p-3 rounded-sm border" data-testid={`bonus-${b.id}`}>
                      <div className="flex-1">
                        <p className="text-sm">
                          Seuil <strong>{formatCurrency(b.threshold_amount)}</strong> → prime{" "}
                          <strong className="text-green-700">{formatCurrency(b.bonus_amount)}</strong>
                        </p>
                        {b.description && <p className="text-xs text-muted-foreground">{b.description}</p>}
                        <p className="text-xs text-muted-foreground">
                          CA actuel : {formatCurrency(b.current_amount || 0)}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={b.achieved ? "border-green-400 text-green-700" : "border-amber-400 text-amber-700"}
                      >
                        {b.achieved ? "Atteint" : "En cours"}
                      </Badge>
                      <Button variant="ghost" size="icon" onClick={() => deleteBonus(b.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SellerObjectivesDialog;
